const regexes = {
	isodate: /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,7}))?Z$/,
	jsondate: /^\/Date\((-?[0-9]+)\)\/$/i,
	uniqueidentifier: /^[a-z0-9]{8}-([a-z0-9]{4}-){3}[a-z0-9]{12}$/i,
	base64: /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/,
};

export type FieldType = {
	/**
	 * Parse data from server as type
	 */
	parse: (value: any) => any;
	/**
	 * Check that a value is of the correct type.
	 */
	validate: (value: any) => boolean;
};

export function guessType(val: any): string {
	// Doesn't really matter which type it is if the value is null
	// Both parsing and validation will pass for any type
	if (val == null) {
		return "string";
	}

	if (Array.isArray(val)) {
		return "array";
	} else if (typeof val === "string") {
		if (regexes.isodate.test(val) || regexes.jsondate.test(val)) {
			return "datetime";
		} else if (regexes.uniqueidentifier.test(val)) {
			return "uniqueidentifier";
		}

		return "string";
	} else if (val instanceof Date) {
		return "datetime";
	}

	let type = typeof val;
	if (["bigint", "number", "boolean"].includes(type)) {
		return type;
	}

	return "string";
}

export function registerType(type: string, fieldType: FieldType) {
	if (!fieldTypes[type]) {
		fieldTypes[type] = fieldType;
	} else {
		console.warn(`Field type '${type}' already registered`);
	}
}

function validateDate(val: any): val is Date | null {
	return (
		val === null ||
		val instanceof Date ||
		(typeof val === "string" && (regexes.isodate.test(val) || regexes.jsondate.test(val))) ||
		typeof val === "number"
	);
}

const ONE_DAY_IN_MS = 86_400_000;

export const fieldTypes: Record<string, FieldType> = {
	string: {
		parse: (value: any) => value.toString(),
		validate: (value: any) => value === null || typeof value === "string",
	},
	number: {
		parse: (value: any) => value,
		validate: (value: any) => value === null || (typeof value === "number" && !isNaN(value)),
	},
	boolean: {
		parse: (value: any) => value,
		validate: (value: any) => value === null || typeof value === "boolean",
	},
	bigint: {
		parse: (value: any) => BigInt(value),
		validate: (value: any) =>
			value === null || typeof value === "string" || typeof value === "bigint" || typeof value === "number",
	},
	uniqueidentifier: {
		parse: (value: any) => value,
		validate: (value: any) => value === null || (typeof value === "string" && regexes.uniqueidentifier.test(value)),
	},
	array: {
		parse: (value: any) => value,
		validate: (value: any) => value === null || Array.isArray(value),
	},
	date: {
		parse: (value: any) => {
			let milliseconds = fieldTypes["datetime"].parse(value).valueOf();

			if (value instanceof Date) {
				milliseconds = +value;
			} else if (typeof value === "string" && !value.indexOf("/Date(")) {
				milliseconds = Number(value.substring(6, value.length - 2));
			} else {
				milliseconds = +new Date(value);
			}

			return new Date(~~(milliseconds / ONE_DAY_IN_MS) * ONE_DAY_IN_MS);
		},
		validate: validateDate,
	},
	datetime: {
		parse: (value: any) => {
			if (value instanceof Date) {
				return value;
			} else if (typeof value === "string" && !value.indexOf("/Date(")) {
				return new Date(Number(value.substring(6, value.length - 2)));
			}
			return new Date(value);
		},
		validate: validateDate,
	},
	time: {
		parse: (value: any) => fieldTypes["datetime"].parse(value),
		validate: validateDate,
	},
	xml: {
		parse: (value: any) => value.toString(),
		validate: (value: any) => value === null || typeof value === "string",
	},
	binary: {
		parse: (value: any) => {
			if (!value) {
				return null;
			}

			let binStr = atob(value);
			let bytes = new Uint8Array(binStr.length);

			for (let i = 0; i < binStr.length; i++) {
				bytes[i] = binStr.charCodeAt(i);
			}

			return bytes.buffer;
		},
		validate: (value: any) => {
			if (value === null || value instanceof ArrayBuffer || value instanceof Uint8Array || value instanceof Blob) {
				return true;
			}

			return regexes.base64.test(value);
		},
	},
};
